import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Pencil, Plus, UserPlus } from "lucide-react";

import { libraryService, type EligibleStudent, type ReadingProgram, type ReadingProgress } from "../../services/libraryService";
import { apiErrorMessage } from "../../utils/apiError";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { PROGRAM_TYPES, day, labelFrom, programConfig, programStatus, targetShare } from "./libraryLogic";
import { SearchPick } from "./Pickers";

const targets = (program: ReadingProgram) =>
  [program.target_books && `${program.target_books} books`, program.target_pages && `${program.target_pages.toLocaleString()} pages`].filter(Boolean).join(" and ") || "No target";

const programColumns: RegisterColumn<ReadingProgram>[] = [
  { header: "Programme", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[12rem] whitespace-normal" },
  { header: "Kind", cell: (row) => labelFrom(PROGRAM_TYPES, row.program_type), exportValue: (row) => labelFrom(PROGRAM_TYPES, row.program_type), className: "text-gray-700" },
  { header: "Runs", cell: (row) => `${row.start_date} → ${row.end_date}`, exportValue: (row) => `${row.start_date} to ${row.end_date}`, className: "whitespace-nowrap tabular-nums text-gray-700" },
  { header: "Target", cell: targets, exportValue: targets, className: "text-gray-700" },
  { header: "Readers", cell: (row) => row.participant_count, exportValue: (row) => row.participant_count, align: "right" },
  { header: "Status", cell: (row) => <StatusDot {...programStatus(row)} />, exportValue: (row) => programStatus(row).label },
];

function Progress({ share }: { share: number | null }) {
  if (share === null) return null;
  return (
    <span className="flex items-center gap-2" aria-label={`${share}% of the target`}>
      <span className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-100">
        <span className="block h-full rounded-full bg-primary-600" style={{ width: `${share}%` }} />
      </span>
      <span className="text-xs tabular-nums text-gray-600">{share}%</span>
    </span>
  );
}

/** Reading challenges, clubs and competitions: who has joined, and how far each reader has got. */
export function ProgramsPanel({ programs, progress, students, isLoading, isError, onRetry, canManage, onChanged }: {
  programs?: ReadingProgram[];
  progress: ReadingProgress[];
  students: EligibleStudent[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<{ record?: ReadingProgram } | null>(null);
  const [joiner, setJoiner] = useState("");
  const [logging, setLogging] = useState<number | null>(null);
  const done = (text: string) => {
    setNotice({ tone: "success", text });
    onChanged();
  };
  const enroll = useMutation({
    mutationFn: ({ program, student }: { program: number; student: number }) => libraryService.enrollReadingProgram(program, student),
    onSuccess: () => {
      setJoiner("");
      done("Reader added to the programme.");
    },
  });
  const log = useMutation({
    mutationFn: ({ id, ...data }: { id: number; books_read: number; pages_read: number; minutes_read: number }) => libraryService.updateReadingProgress(id, data),
    onSuccess: () => {
      setLogging(null);
      done("Reading added.");
    },
  });

  const program = viewing !== null && !editing ? programs?.find((row) => row.id === viewing) : undefined;
  const readers = program ? progress.filter((row) => row.program === program.id).sort((a, b) => b.books_read - a.books_read || b.pages_read - a.pages_read) : [];
  const joinable = program ? students.filter((student) => !readers.some((row) => row.student === student.id)) : [];
  const running = program ? programStatus(program).label !== "Ended" && program.is_active : false;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Reading programmes"
        description={canManage ? "Challenges, clubs and competitions. Open one to add readers and record their reading." : "Programmes open to join, and how far you have got."}
        actions={canManage && (
          <button type="button" onClick={() => setEditing({})} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> New programme
          </button>
        )}
        rows={programs}
        config={programConfig}
        columns={programColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          setJoiner("");
          enroll.reset();
          setViewing(row.id);
        }}
        noun={["programme", "programmes"]}
        exportName={{ file: "reading-programmes", title: "Reading Programmes" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No reading programmes yet. Start one to get pupils reading." : "No reading programmes are open."}
      />

      {program && (
        <DetailsModal
          size="3xl"
          title={program.name}
          headline={{ label: "Readers", value: String(program.participant_count), note: `${labelFrom(PROGRAM_TYPES, program.program_type)} · ${program.start_date} to ${program.end_date}` }}
          status={programStatus(program)}
          facts={[
            ["Kind", labelFrom(PROGRAM_TYPES, program.program_type)],
            ["Runs", `${program.start_date} to ${program.end_date}`],
            ["Target", targets(program)],
            ["Finished", String(readers.filter((row) => row.is_completed).length)],
          ]}
          sections={[{ title: "About it", body: program.description }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing({ record: program })}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        >
          {running && joinable.length > 0 && (
            <ActionPanel title={canManage ? "Add a reader" : "Join"} description={canManage ? "Any pupil can take part." : "Sign up to take part."}>
              <form
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  if (joiner) enroll.mutate({ program: program.id, student: Number(joiner) });
                }}
                className="space-y-3"
              >
                <Field as="div" label="Pupil">
                  <SearchPick required items={joinable} value={joiner} onChange={setJoiner} label={(item) => item.full_name} detail={(item) => item.current_class_name ?? ""} placeholder="Type a name or class" empty="No pupil left to add" />
                </Field>
                <div className="flex justify-end">
                  <button disabled={!joiner || enroll.isPending} className="btn btn-primary gap-1.5"><UserPlus className="h-4 w-4" /> {canManage ? "Add reader" : "Join"}</button>
                </div>
                {enroll.isError && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{apiErrorMessage(enroll.error, "They could not be added.")}</p>}
              </form>
            </ActionPanel>
          )}
          <section>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Readers</h4>
            {readers.length ? (
              <ol className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {readers.map((row) => (
                  <li key={row.id} className="space-y-2 px-4 py-2.5 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <span className="font-medium text-gray-900">{row.student_name}</span>
                      <span className="text-xs text-gray-500">{row.books_read} books · {row.pages_read.toLocaleString()} pages · {row.minutes_read.toLocaleString()} minutes</span>
                      <span className="flex items-center gap-3">
                        {row.is_completed ? <StatusDot label={`Finished ${day(row.completion_date)}`} dot="bg-emerald-500" /> : <Progress share={targetShare(row, program)} />}
                        {canManage && !row.is_completed && running && (
                          <button type="button" onClick={() => setLogging(logging === row.id ? null : row.id)} className="text-xs font-medium text-primary-700 hover:underline">
                            Add reading
                          </button>
                        )}
                      </span>
                    </div>
                    {logging === row.id && (
                      <form
                        onSubmit={(event: FormEvent<HTMLFormElement>) => {
                          event.preventDefault();
                          const data = new FormData(event.currentTarget);
                          log.mutate({ id: row.id, books_read: Number(data.get("books") || 0), pages_read: Number(data.get("pages") || 0), minutes_read: Number(data.get("minutes") || 0) });
                        }}
                        className="grid gap-2 rounded-md bg-gray-50 p-3 sm:grid-cols-4 sm:items-end"
                      >
                        <Field label="Books finished"><input type="number" min={0} name="books" defaultValue={0} className="input mt-1 tabular-nums" /></Field>
                        <Field label="Pages read"><input type="number" min={0} name="pages" defaultValue={0} className="input mt-1 tabular-nums" /></Field>
                        <Field label="Minutes"><input type="number" min={0} name="minutes" defaultValue={0} className="input mt-1 tabular-nums" /></Field>
                        <button disabled={log.isPending} className="btn btn-primary">Add</button>
                        {log.isError && <p role="alert" className="text-sm text-red-700 sm:col-span-4">{apiErrorMessage(log.error, "The reading could not be added.")}</p>}
                      </form>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-gray-500">Nobody has joined yet.</p>
            )}
          </section>
        </DetailsModal>
      )}

      {editing && (
        <ProgramForm
          record={editing.record}
          onClose={() => setEditing(null)}
          onDone={(saved, created) => {
            setEditing(null);
            setViewing(saved.id);
            done(created ? `${saved.name} started.` : `${saved.name} saved.`);
          }}
        />
      )}
    </div>
  );
}

function ProgramForm({ record, onClose, onDone }: { record?: ReadingProgram; onClose: () => void; onDone: (program: ReadingProgram, created: boolean) => void }) {
  const [name, setName] = useState(record?.name ?? "");
  const [kind, setKind] = useState(record?.program_type ?? "challenge");
  const [start, setStart] = useState(record?.start_date ?? "");
  const [end, setEnd] = useState(record?.end_date ?? "");
  const [books, setBooks] = useState(record?.target_books ? String(record.target_books) : "");
  const [pages, setPages] = useState(record?.target_pages ? String(record.target_pages) : "");
  const [description, setDescription] = useState(record?.description ?? "");
  const wrongWay = Boolean(start && end && end < start);
  const save = useMutation({
    mutationFn: (data: Partial<ReadingProgram>) => (record ? libraryService.updateReadingProgram(record.id, data) : libraryService.createReadingProgram({ ...data, is_active: true })),
    onSuccess: (saved) => onDone(saved, !record),
  });
  return (
    <FormModal
      id="program-form"
      title={record ? `Edit ${record.name}` : "New reading programme"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The programme could not be saved.") : undefined}
      saveLabel={record ? "Save changes" : "Start programme"}
      onClose={onClose}
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (wrongWay || !description.trim()) return;
        save.mutate({
          name: name.trim(),
          program_type: kind,
          start_date: start,
          end_date: end,
          target_books: Number(books) || null,
          target_pages: Number(pages) || null,
          description: description.trim(),
        });
      }}
    >
      <FormSection title="Programme" divided={false} columns={2}>
        <Field label="Name" className="sm:col-span-2">
          <input required autoFocus value={name} onChange={(event) => setName(event.target.value)} className="input mt-1" placeholder="e.g. Term 3 Reading Challenge" />
        </Field>
        <Field label="Kind">
          <select value={kind} onChange={(event) => setKind(event.target.value)} className="input mt-1">
            {PROGRAM_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <div />
        <Field label="Starts">
          <input required type="date" value={start} onChange={(event) => setStart(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Ends" hint={wrongWay ? <span className="text-red-700">It must end after it starts.</span> : undefined}>
          <input required type="date" min={start || undefined} value={end} onChange={(event) => setEnd(event.target.value)} className="input mt-1" />
        </Field>
      </FormSection>
      <FormSection title="Target" description="Optional. A reader finishes on reaching every target set." columns={2}>
        <Field label="Books to read" optional>
          <input type="number" min={1} value={books} onChange={(event) => setBooks(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Pages to read" optional>
          <input type="number" min={1} value={pages} onChange={(event) => setPages(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>
      <FormSection title="About it" description="Pupils and parents read this." columns={1}>
        <Field as="div" label="Description">
          <AIAssistField
            rows={3}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="What readers do, and what they are aiming for"
            context={{
              key: "library.note",
              params: { kind: "program", name, program_type: kind, start_date: start, end_date: end, target_books: books || null, target_pages: pages || null },
            }}
            draftLabel="Draft description"
            draftHint={name.trim() ? "Uses the name, kind, dates and targets above." : "Name the programme first, then draft."}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}
